import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import {
  Home,
  Globe,
  FileText,
  Mail,
  MoreHorizontal,
  X,
  Info,
  HelpCircle,
  MessageSquare,
  User,
  LogIn,
  LogOut,
  Settings,
  Briefcase,
  LayoutDashboard,
} from 'lucide-react';
import { ADMIN_NAV_GROUPS } from '../../config/adminNav';

type IconType = React.ComponentType<{ className?: string }>;

interface PrimaryItem {
  key: string;
  label: string;
  href: string;
  icon: IconType;
  match: (pathname: string) => boolean;
}

interface SheetLink {
  key: string;
  label: string;
  href?: string;
  icon: IconType;
  onClick?: () => void;
  danger?: boolean;
}

const MobileBottomNav: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { isAuthenticated, isAdmin, isSuperAdmin, user, logout } = useAuth();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  useEscapeKey(() => setIsSheetOpen(false), isSheetOpen);

  useEffect(() => {
    setIsSheetOpen(false);
  }, [location.pathname]);

  const isAdminContext = isAdmin && location.pathname.startsWith('/admin');

  const primaryItems: PrimaryItem[] = isAdminContext
    ? [
        {
          key: 'dashboard',
          label: t('admin.dashboard'),
          href: '/admin',
          icon: LayoutDashboard,
          match: (p) => p === '/admin',
        },
        {
          key: 'crm',
          label: t('admin.crm'),
          href: '/admin/crm/clientes',
          icon: Briefcase,
          match: (p) =>
            p.startsWith('/admin/crm') ||
            p.startsWith('/admin/clientes') ||
            p.startsWith('/admin/vendas') ||
            p.startsWith('/admin/pagamentos'),
        },
        {
          key: 'posts',
          label: t('admin.posts'),
          href: '/admin/posts',
          icon: FileText,
          match: (p) => p.startsWith('/admin/posts'),
        },
        {
          key: 'contacts',
          label: t('admin.contacts'),
          href: '/admin/contacts',
          icon: Mail,
          match: (p) => p.startsWith('/admin/contacts'),
        },
      ]
    : [
        { key: 'home', label: t('nav.home'), href: '/', icon: Home, match: (p) => p === '/' },
        {
          key: 'immigration',
          label: t('nav.immigration'),
          href: '/imigracao',
          icon: Globe,
          match: (p) => p.startsWith('/imigracao'),
        },
        {
          key: 'blog',
          label: t('nav.blog'),
          href: '/blog',
          icon: FileText,
          match: (p) => p.startsWith('/blog') || p.startsWith('/post/'),
        },
        {
          key: 'contact',
          label: t('nav.contact'),
          href: '/contato',
          icon: Mail,
          match: (p) => p.startsWith('/contato'),
        },
      ];

  // Posts/contatos/CRM já aparecem em primaryItems (bottom bar) — não duplicar no sheet "Mais".
  const primaryItemKeys = new Set(['posts', 'contacts', 'crm']);

  const sheetLinks: SheetLink[] = isAdminContext
    ? [
        ...ADMIN_NAV_GROUPS.flatMap((group) => group.items)
          .filter((item) => !primaryItemKeys.has(item.key))
          .filter((item) => !item.requiredRole || (item.requiredRole === 'super_admin' && isSuperAdmin))
          .map((item) => ({ key: item.key, label: t(item.labelKey), href: item.href, icon: item.icon })),
        { key: 'logout', label: t('nav.logout'), icon: LogOut, danger: true, onClick: () => logout() },
      ]
    : [
        { key: 'about', label: t('nav.about'), href: '/sobre', icon: Info },
        { key: 'faq', label: t('nav.faq'), href: '/faq', icon: HelpCircle },
        { key: 'testimonials', label: t('nav.testimonials'), href: '/depoimentos', icon: MessageSquare },
        { key: 'nif', label: t('nav.nif'), href: '/nif', icon: FileText },
        ...(isAuthenticated
          ? [
              ...(isAdmin ? [{ key: 'admin', label: t('nav.admin'), href: '/admin', icon: Settings }] : []),
              { key: 'logout', label: t('nav.logout'), icon: LogOut, danger: true, onClick: () => logout() },
            ]
          : [{ key: 'login', label: t('nav.login'), href: '/login', icon: LogIn }]),
      ];

  const isMoreActive = !primaryItems.some((item) => item.match(location.pathname));

  return (
    <>
      {isSheetOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-[55] lg:hidden"
          onClick={() => setIsSheetOpen(false)}
          aria-hidden="true"
        />
      )}

      {isSheetOpen && (
        <div
          className="fixed inset-x-0 bottom-0 z-[60] lg:hidden bg-white rounded-t-2xl shadow-[0_-8px_30px_rgba(0,0,0,0.15)] flex flex-col"
          style={{ maxHeight: '75vh' }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
            <span className="text-sm font-semibold text-gray-900">{t('nav.more')}</span>
            <button
              type="button"
              onClick={() => setIsSheetOpen(false)}
              className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100"
              aria-label="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div
            className="overflow-y-auto px-2 py-2"
            style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
          >
            {isAuthenticated && !isAdminContext && (
              <div className="flex items-center gap-3 px-3 py-3 mb-1 border-b border-gray-100">
                <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
            )}
            {sheetLinks.map((link) =>
              link.href ? (
                <Link
                  key={link.key}
                  to={link.href}
                  className="flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  <link.icon className="w-5 h-5 text-gray-500" />
                  <span className="text-sm font-medium">{link.label}</span>
                </Link>
              ) : (
                <button
                  key={link.key}
                  type="button"
                  onClick={link.onClick}
                  className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg ${
                    link.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{link.label}</span>
                </button>
              )
            )}
          </div>
        </div>
      )}

      <nav
        className="fixed inset-x-0 bottom-0 z-40 lg:hidden bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'max(0.25rem, env(safe-area-inset-bottom))' }}
      >
        <div className="grid grid-cols-5 h-16">
          {primaryItems.map((item) => {
            const active = item.match(location.pathname);
            return (
              <Link
                key={item.key}
                to={item.href}
                className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
                  active ? 'text-primary-600' : 'text-gray-500'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="truncate max-w-[4.2rem]">{item.label}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setIsSheetOpen((prev) => !prev)}
            className={`flex flex-col items-center justify-center gap-0.5 text-[11px] font-medium transition-colors ${
              isSheetOpen || isMoreActive ? 'text-primary-600' : 'text-gray-500'
            }`}
          >
            <MoreHorizontal className="w-5 h-5" />
            <span>{t('nav.more')}</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
